/**
 * <ShareForkBanner> — informational banner that renders above the
 * BuilderTabBar on a conversation that was created via fork from a shared
 * deal. Shows the lineage ("Forked from <parentTitle> by <ownerName>"),
 * surfaces the [Compare versions] CTA (Phase 6 Session 6.2 wires
 * `<VersionDiffModal>`), and dispatches the `arcanum:share-back` window
 * event from [Share back].
 *
 * `external-sharing` Phase 4 Session 4.3 (2026-05-09); [Compare versions]
 * un-disabled in Phase 6 Session 6.2 (2026-05-09).
 *
 * **Distinct file** from the existing `apps/web/src/components/builder/
 * ForkBanner.tsx` (model-version-conflict banner from
 * `agent-ux-optimization` — different concern; do not merge or replace).
 * Naming collision was caught at /gap-analysis 2026-05-09; the banner
 * lineage component is `<ShareForkBanner>` to keep both surfaces distinct.
 *
 * Hides itself (returns null) when `forkedFromShareId` is null — the
 * conversation was created via the normal flow, not via a fork.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { formatRelativeTime } from '@/lib/format';
import { usePublicDeal } from '@/hooks/use-public-deal';
import { VersionDiffModal } from '@/components/sharing';
import type { DiffSide } from '@arcanum-hq/shared-types';

export interface ShareForkBannerProps {
  /** The forked conversation's id — used as the `forkConversationId`
   *  payload on the `arcanum:share-back` event so `<ShareBackModal>`
   *  (Session 4.4) can identify which conversation to share back. */
  conversationId: string;
  /** The parent share's id (`conversations.forked_from_share_id`).
   *  When null, the banner doesn't render. */
  forkedFromShareId: string | null;
  /** The forked-from conversation id — required for [Compare versions] to
   *  diff parent conv ↔ child conv. Sourced from
   *  `conversations.forked_from_conversation_id` (Phase 1 column; projected
   *  by `conversations.service.v2.ts:get` since Phase 4 Session 4.3). */
  forkedFromConversationId?: string | null;
  className?: string;
}

export function ShareForkBanner({
  conversationId,
  forkedFromShareId,
  forkedFromConversationId = null,
  className,
}: ShareForkBannerProps) {
  // Hide for non-forked conversations. The parent route mounts the banner
  // unconditionally; this gate keeps the call site simple.
  const parentDealQuery = usePublicDeal(forkedFromShareId);
  const [diffOpen, setDiffOpen] = useState(false);

  if (forkedFromShareId === null) return null;

  const parentTitle = parentDealQuery.data?.title;
  const ownerName = parentDealQuery.data?.ownerName;
  const createdAt = parentDealQuery.data?.createdAt;

  function handleShareBack(): void {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(
        new CustomEvent('arcanum:share-back', {
          detail: {
            forkConversationId: conversationId,
            parentShareId: forkedFromShareId,
          },
        }),
      );
    } catch {
      // Defensive — environments without CustomEvent support shouldn't crash.
    }
  }

  // [Compare versions] is always enabled when forkedFromShareId is present
  // (banner-render gate above). Diff sides:
  // - LEFT (parent): use `frozen_state` keyed by the parent share id. The
  //   recipient is, by definition, a share-recipient — they have share
  //   access to the frozen snapshot but NOT direct conversation read access
  //   (e2e walkthrough Phase 6 surfaced: server's diff endpoint requires
  //   conversation-level access on both sides; recipients hit 403 with
  //   `kind: 'conversation'` on the parent).
  // - RIGHT (this fork): the caller owns the fork, so `kind: 'conversation'`
  //   works — they have native read access.
  const compareEnabled = true;
  const diffFrom: DiffSide = { kind: 'frozen_state', id: forkedFromShareId };
  const diffTo: DiffSide = { kind: 'conversation', id: conversationId };

  return (
    <>
    <div
      data-testid="share-fork-banner"
      data-fork-conversation-id={conversationId}
      data-parent-share-id={forkedFromShareId}
      className={clsx(
        'flex items-center justify-between gap-4 px-4 py-2.5',
        'bg-blue-dim border border-blue/15 rounded-[3px]',
        'text-blue shrink-0',
        className,
      )}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <GitBranch size={14} className="text-blue shrink-0" />
        <span className="text-sm font-body text-text-bright truncate">
          <span className="font-mono text-blue uppercase tracking-[0.5px] text-micro">
            FORKED
          </span>
          <span className="text-text-muted mx-2">·</span>
          {parentTitle ? (
            <>
              <span className="text-text-muted">from </span>
              <span className="font-mono text-text-bright truncate">{parentTitle}</span>
              {ownerName && (
                <>
                  <span className="text-text-muted"> by </span>
                  <span className="font-mono text-text-bright">{ownerName}</span>
                </>
              )}
              {createdAt && (
                <>
                  <span className="text-text-muted"> · </span>
                  <span className="font-mono text-text-muted">
                    {formatRelativeTime(createdAt)}
                  </span>
                </>
              )}
            </>
          ) : parentDealQuery.isLoading ? (
            <span className="text-text-muted italic">loading lineage…</span>
          ) : (
            <span className="text-text-muted italic">parent share unavailable</span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          variant="secondary"
          onClick={() => setDiffOpen(true)}
          disabled={!compareEnabled}
          title="Compare this fork against the parent share's snapshot"
          data-testid="share-fork-banner-compare"
          className="!py-1.5 !px-3"
        >
          Compare versions
        </Button>
        <Button
          variant="primary"
          onClick={handleShareBack}
          data-testid="share-fork-banner-share-back"
          className="!py-1.5 !px-3"
        >
          Share back
        </Button>
      </div>
    </div>
    <VersionDiffModal
      open={diffOpen}
      from={diffFrom}
      to={diffTo}
      fromLabel={parentDealQuery.data?.title ?? 'Parent'}
      toLabel="This fork"
      onClose={() => setDiffOpen(false)}
    />
    </>
  );
}
